import SyosEvents from './utils/SyosEvents';
import templatePriceTypeSelectionPopup from './templates/pricetype-selection-popup-template';
import { loadModal } from '../AsyncLoader';
import dom from '../utils/dom';
import type SyosController from './SyosController';
import type SyosSeat from './SyosSeat';
import type ModalHandle from '../tnew/ui/Modal/ModalHandle';
import { IPriceTypeSelectionHelper } from './priceTypeSelectionHelpers/types';
import { ISyosLabels } from './labels/types';
import { OrderLevelFeeDisclaimerConfig } from './templates/renderOrderLevelFeeDisclaimer';

class SyosPriceTypeSelector {
  modal?: ModalHandle;

  seat?: SyosSeat;

  $priceTypeButtons?: Element[];

  $modalCloseButton?: HTMLButtonElement;

  syosController?: SyosController;

  private orderLevelFeeDisclaimerConfig: OrderLevelFeeDisclaimerConfig;

  constructor(syosController: SyosController, private labels: ISyosLabels, private priceTypeSelectionHelper: IPriceTypeSelectionHelper) {
    this.syosController = syosController;
    this.orderLevelFeeDisclaimerConfig = {
      isOrderLevelFeeDisclaimerDisplayed: syosController.config.isOrderLevelFeeDisclaimerDisplayed,
      orderLevelFeeDisclaimerText: syosController.config.orderLevelFeeDisclaimerText,
    };

    this.syosController.on(SyosEvents.SEAT_CLICK, this.onSeatClick);
    this.syosController.on(SyosEvents.SCREEN_UPDATED, this.onScreenUpdated);
  }

  onSeatClick = async ({ seat }: { seat: SyosSeat }) => {
    if (!this.priceTypeSelectionHelper.canMakeAnotherSelection) {
      return;
    }

    this.seat = seat;

    const { labels } = this;

    const Modal = await loadModal();

    if (this.priceTypeSelectionHelper.mode === 'flex') {
      const filteredPriceTypes = this.priceTypeSelectionHelper.getFilteredPriceTypes(seat.getPriceTypes());

      const nextPriceType = filteredPriceTypes[0];

      if (!nextPriceType) {
        return;
      }

      const html = templatePriceTypeSelectionPopup.render(this.labels, seat, this.priceTypeSelectionHelper, this.orderLevelFeeDisclaimerConfig);

      const $el = dom.parseElement(html)!;

      Modal.confirm({
        title: labels.selectSeatHeading,
        returnFocusTo: seat.$el,
        className: 'tn-syos-price-types-selector-modal',
        bodyContents: $el,
        labelBtnCancel: labels.selectSeatCloseButtonLabel,
        labelBtnConfirm: labels.selectSeatSubmitButtonLabel,
        onConfirm: () => {
          seat.selectPriceType(nextPriceType.price_type);
        },
        beforeRemove: () => {
          this.syosController?.emit(SyosEvents.MODAL_CLOSE);
        },
      });

      return;
    }

    const html = templatePriceTypeSelectionPopup.render(this.labels, seat, this.priceTypeSelectionHelper, this.orderLevelFeeDisclaimerConfig);

    const $el = dom.parseElement(html)!;

    this.$priceTypeButtons = dom.findAllFrom($el, '[data-price-type-id]');

    this.$priceTypeButtons.forEach(async ($button) => {
      $button.addEventListener('click', this.onPriceTypeButtonClick);
    });

    // eslint-disable-next-line prefer-destructuring
    this.$modalCloseButton = Modal.utils.makeSecondaryButton(labels.selectSeatCloseButtonLabel)[0];

    this.$modalCloseButton.addEventListener('click', this.onModalCloseButtonClick);

    this.modal = Modal.open({
      title: labels.selectSeatHeading,
      bodyContents: $el,
      returnFocusTo: seat.$el,
      className: 'tn-syos-price-types-selector-modal',
      footerContents: this.$modalCloseButton,
      beforeClose: this.onModalClose,
      beforeRemove: () => {
        this.syosController?.emit(SyosEvents.MODAL_CLOSE);
      },
    });
  };

  onPriceTypeButtonClick = (event: Event) => {
    event.preventDefault();

    const $button = event.currentTarget as HTMLButtonElement;

    const priceTypeId = parseInt($button.getAttribute('data-price-type-id')!, 10);

    this.seat?.selectPriceType(priceTypeId);

    if (this.modal) {
      this.modal.close();
    }
  };

  onModalCloseButtonClick = () => {
    if (this.modal) {
      this.modal.close();
    }
  };

  onModalClose = () => {
    this.modal = undefined;
    this.$modalCloseButton?.removeEventListener('click', this.onModalCloseButtonClick);
    this.$modalCloseButton = undefined;
    this.$priceTypeButtons?.forEach(($el) => $el.removeEventListener('click', this.onPriceTypeButtonClick));
    this.$priceTypeButtons = undefined;
    this.seat = undefined;
  };

  onScreenUpdated = () => {
    if (this.modal) {
      this.modal.close();
    }
  };

  destroy() {
    this.syosController = undefined;
  }
}

export default SyosPriceTypeSelector;
